/**
 * useSubscription — Phase 3 hook.
 *
 * Provides the current workspace's subscription state with auto-refresh.
 * Returns:
 *   { loading, subscription, pricingModel, workspace, reload, error }
 *
 * Backed by a module-level shared store: every consumer of this hook reads the
 * same state, and a reload() from ANY consumer refreshes ALL of them. This is
 * deliberate — previously each call had its own useState, so reactivating from
 * the Plan & Storage tab refreshed only that tab while the top-of-app
 * "Subscription cancelled" banner (a separate useSubscription instance in
 * AppShell) kept showing stale state until a full page reload. Sharing the
 * store keeps the banner and the settings page in sync instantly.
 */

import { useCallback, useEffect, useState } from 'react'
import { subscriptionAPI } from '../services/subscription'

const INITIAL = {
  loading: true,
  subscription: null,
  pricingModel: 'drive_first',
  workspace: null,
  error: null,
}

let sharedState = INITIAL
const listeners = new Set()
let inflight = null

function emit() {
  for (const listener of listeners) listener(sharedState)
}

// Loads subscription state into the shared store and notifies every mounted
// consumer. Concurrent calls are de-duped so N components mounting at once (or
// a reload racing an initial load) issue a single network request.
function loadShared() {
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const res = await subscriptionAPI.me()
      const payload = res?.data?.data ?? res?.data ?? null
      if (res?.success && payload) {
        sharedState = {
          loading: false,
          subscription: payload.subscription,
          pricingModel: payload.pricing_model,
          workspace: payload.workspace,
          error: null,
        }
      } else {
        sharedState = { ...sharedState, loading: false, error: res?.error?.message || 'Failed to load' }
      }
    } catch (e) {
      sharedState = { ...sharedState, loading: false, error: e?.message || 'Failed to load' }
    } finally {
      inflight = null
      emit()
    }
  })()
  return inflight
}

export function useSubscription({ pollIntervalMs = 0 } = {}) {
  const [state, setState] = useState(sharedState)

  useEffect(() => {
    listeners.add(setState)
    // Sync to the latest shared snapshot on mount, then revalidate in the
    // background so navigating to a view (or switching workspace, which
    // remounts consumers) always reflects current server state.
    setState(sharedState)
    loadShared()
    return () => { listeners.delete(setState) }
  }, [])

  const reload = useCallback(() => loadShared(), [])

  useEffect(() => {
    if (!pollIntervalMs || pollIntervalMs < 1000) return undefined
    const t = setInterval(() => loadShared(), pollIntervalMs)
    return () => clearInterval(t)
  }, [pollIntervalMs])

  return { ...state, reload }
}
